import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MONO } from '../../constants/fonts';
import { useAuth } from '../../context/AuthContext';
import { useLoginModal } from '../../context/LoginModalContext';
import { isDdhappsAccount, landingRouteFor } from '../../utils/auth';
import WelcomeOfferBanner from './WelcomeOfferBanner';

function HomeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
      <polyline points="9 21 9 12 15 12 15 21" />
    </svg>
  );
}

function SiteNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { openLogin } = useLoginModal();
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const lastScrollY = React.useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY;
      const delta = current - lastScrollY.current;
      setScrolled(current > 10);
      if (current <= 60) {
        setVisible(true);
      } else if (Math.abs(delta) > 6) {
        setVisible(delta < 0);
        lastScrollY.current = current;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome    = pathname === '/';
  const isPricing = pathname === '/pricing';
  const isContact = pathname === '/contact';
  const isAccountRoute = pathname === '/account' || pathname === '/admin';
  const isAdmin = pathname === '/admin';

  const logoSpan = (
    <span style={s.logo}>
      <span style={s.logoDDH}>DDH</span>
      <span style={s.logoBracket}>[</span>
      <span style={s.logoApps}>APPS</span>
      <span style={s.logoBracket}>]</span>
      <span className="cursor" style={s.logoCursor}>_</span>
    </span>
  );

  return (
    <div style={{
      ...s.navWrapper,
      transform: visible ? 'translateY(0)' : 'translateY(-100%)',
    }}>
      <nav className={`site-nav${scrolled ? ' scrolled' : ''}`} style={{
        ...s.nav,
        boxShadow: scrolled ? '0 1px 12px rgba(0,0,0,0.07)' : 'none',
      }}>
        <div className="nav-logo">
          {isHome
            ? <span onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>{logoSpan}</span>
            : <Link to="/" style={{ textDecoration: 'none' }}>{logoSpan}</Link>}
        </div>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/" className="nav-link" aria-label="Home"
            onClick={() => isHome && window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{ ...s.iconLink, color: isHome ? '#2a2a2a' : '#555', border: isHome ? '1.5px solid #888' : '1px solid #ddd' }}>
            <HomeIcon />
          </Link>
          <Link to="/pricing" className="nav-link"
            onClick={() => isPricing && window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{ ...s.navLink, fontWeight: isPricing ? 600 : 500, color: isPricing ? '#2a2a2a' : '#555', border: isPricing ? '1.5px solid #888' : '1px solid #ddd' }}>
            Pricing
          </Link>
          <Link to="/contact" className="nav-link"
            onClick={() => isContact && window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{ ...s.navLink, fontWeight: isContact ? 600 : 500, color: isContact ? '#2a2a2a' : '#555', border: isContact ? '1.5px solid #888' : '1px solid #ddd' }}>
            Get in touch
          </Link>
        </div>
        {user ? (
          <Link to={landingRouteFor(user)} className="nav-link nav-account"
            style={{ ...s.navLink, fontWeight: isAccountRoute ? 600 : 500, color: isAccountRoute ? '#2a2a2a' : '#555', border: isAccountRoute ? '1.5px solid #888' : '1px solid #ddd' }}>
            {isDdhappsAccount(user) ? 'Admin' : 'Account'}
          </Link>
        ) : (
          <button type="button" className="nav-link nav-account" onClick={openLogin} style={{ ...s.navLink, ...s.navButton }}>
            Log in
          </button>
        )}
      </nav>
      {!isAdmin && <WelcomeOfferBanner />}
    </div>
  );
}

const s = {
  navWrapper: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    transition: 'transform 0.3s ease',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '18px 32px',
    borderBottom: '1px solid #ebebeb',
    background: '#f9f9f7',
    transition: 'box-shadow 0.2s ease',
  },
  logo: {
    fontFamily: MONO,
    fontSize: '0.9rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: '#2a2a2a',
    userSelect: 'none',
  },
  logoDDH: { color: '#2a2a2a' },
  logoBracket: { color: '#9ca3af' },
  logoApps: { color: '#555' },
  logoCursor: { color: '#9ca3af', fontFamily: MONO },
  iconLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '7px 10px',
    border: '1px solid #ddd',
    borderRadius: 8,
    textDecoration: 'none',
    transition: 'border-color 0.15s, color 0.15s',
  },
  navLink: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#555',
    textDecoration: 'none',
    padding: '7px 16px',
    border: '1px solid #ddd',
    borderRadius: 8,
    transition: 'border-color 0.15s, color 0.15s',
  },
  navButton: {
    background: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
};

export default SiteNav;
